import React, { useContext, useRef, useCallback } from 'react'
import { useField } from 'formik'

import { RadioGroup, XStack, Text, styled } from 'tamagui'

import { FormFieldContext } from './shared'

type RadioOption = {
  label: string
  value: string
}

type FormRadioGroupProps = {
  options: RadioOption[]
}

const StyledRadioGroupItem = styled(RadioGroup.Item, {
  borderColor: '$grey6',
  borderWidth: 2,
  width: 20,
  height: 20,
  borderRadius: 100,
  backgroundColor: '$grey0',
  padding: 0,
  alignItems: 'center',
  justifyContent: 'center',

  variants: {
    checked: {
      true: {
        borderColor: '$darkBlue9',
      },
    },
    disabled: {
      true: {},
    },
  },
} as const)

const StyledRadioGroupIndicator = styled(RadioGroup.Indicator, {
  width: 10,
  height: 10,
  borderRadius: 100,
  backgroundColor: '$darkBlue9',
} as const)

export const FormRadioGroup = ({ options }: FormRadioGroupProps) => {
  const formFieldContext = useContext(FormFieldContext)
  const [field, , helpers] = useField(formFieldContext.name)
  const itemRefs = useRef<Record<string, HTMLElement | null>>({})

  const handleRowPress = useCallback((optionValue: string) => {
    // Programmatically click the RadioGroup.Item button so selection goes
    // through the RadioGroup's own state management (single source of truth).
    const button = itemRefs.current[optionValue]
    if (button && 'click' in button) {
      ;(button as HTMLElement).click()
    }
  }, [])

  return (
    <RadioGroup
      value={field.value}
      onValueChange={(value) => {
        helpers.setTouched(true)
        helpers.setValue(value)
      }}
    >
      {options.map((option) => {
        const isSelected = field.value === option.value

        return (
          <XStack
            key={option.value}
            alignItems='center'
            gap='$3'
            paddingVertical='$1'
          >
            <StyledRadioGroupItem
              ref={(el: HTMLElement | null) => { itemRefs.current[option.value] = el }}
              value={option.value}
              id={`${formFieldContext.name}-${option.value}`}
              checked={isSelected}
            >
              <StyledRadioGroupIndicator />
            </StyledRadioGroupItem>
            <Text
              fontSize='$5'
              cursor='pointer'
              // @ts-expect-error onClick is web-only
              onClick={() => handleRowPress(option.value)}
            >
              {option.label}
            </Text>
          </XStack>
        )
      })}
    </RadioGroup>
  )
}
